import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { fetchEpisode } from "../../../api/peContent";
import type { Lang, Tier } from "../../../api/serving";

/**
 * Whole-class presentation / projection route (Phase 2.E).
 *
 * A thin, teacher-only, CHROME-FREE full-bleed surface to throw a chapter's
 * animated episode on a classroom screen. Registered top-level (NOT under
 * DashboardLayout) so no global header steals vertical space.
 *
 * PULL-ONLY: it only READS pe_episodes via the canonical `fetchEpisode`
 * resolver (FE → Supabase direct, RLS-gated). It writes nothing — no progress,
 * no assignment rows, no AGS/passback. It does not touch the student delivery
 * path (StudentLessonPage / LessonSequence / LTI).
 *
 * Resolve is the same (chapterId, tier, lang) → vimeo_id triple the loop's
 * Watch step (`EpisodeStep`) uses; chapterId comes from the route, tier+lang
 * from query params (default T1/en). The embed reuses EpisodeStep's iframe
 * pattern but with autoplay=1 and no header/card (spec §3, projection cut).
 */

const TIERS: Tier[] = ["T1", "T2", "T3", "T4"];
const LANGS: Lang[] = ["en", "es"];

const PresentationRoute: React.FC = () => {
  const { chapterId } = useParams<{ chapterId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // Tier/lang seed from query, defaulting T1/en; tolerant of case.
  const rawTier = (searchParams.get("tier") ?? "T1").toUpperCase();
  const tier: Tier = (TIERS as string[]).includes(rawTier) ? (rawTier as Tier) : "T1";
  const rawLang = (searchParams.get("lang") ?? "en").toLowerCase();
  const lang: Lang = rawLang === "es" ? "es" : "en";

  const [vimeoId, setVimeoId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!chapterId) {
      setLoading(false);
      setVimeoId(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    // READ-ONLY resolve. fetchEpisode is the canonical chapter→vimeo resolver.
    fetchEpisode(chapterId, tier, lang)
      .then((e) => { if (!cancelled) setVimeoId(e?.vimeoId ?? null); })
      .catch((err) => {
        console.warn("[PresentationRoute] episode load failed:", err);
        if (!cancelled) setVimeoId(null);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [chapterId, tier, lang]);

  // Switch tier/lang live + keep the URL query in sync so reload is stable.
  const updateParam = (key: "tier" | "lang", value: string) => {
    const next = new URLSearchParams(searchParams);
    next.set(key, value);
    setSearchParams(next, { replace: true });
  };

  const exit = () => {
    // navigate(-1); fall back to /teacher when there's no history to pop.
    if (window.history.length > 1) navigate(-1);
    else navigate("/teacher");
  };

  return (
    <div className="fixed inset-0 bg-black flex items-center justify-center">
      {/* Minimal teacher overlay — corner, unobtrusive, legible on black.
          Always rendered so tier/lang can be switched from any state. */}
      <div className="absolute top-3 right-3 z-20 flex items-center gap-3 rounded-full bg-black/60 backdrop-blur px-3 py-1.5 text-white select-none">
        <div className="flex items-center gap-1" role="group" aria-label="Tier">
          {TIERS.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => updateParam("tier", t)}
              aria-pressed={tier === t}
              className={`text-xs font-semibold rounded px-1.5 py-0.5 transition-colors ${
                tier === t ? "bg-white text-black" : "text-gray-300 hover:text-white"
              }`}
            >
              {t}
            </button>
          ))}
        </div>
        <span className="w-px h-4 bg-white/30" aria-hidden />
        <div className="flex items-center gap-1" role="group" aria-label="Language">
          {LANGS.map((l) => (
            <button
              key={l}
              type="button"
              onClick={() => updateParam("lang", l)}
              aria-pressed={lang === l}
              className={`text-xs font-semibold rounded px-1.5 py-0.5 uppercase transition-colors ${
                lang === l ? "bg-white text-black" : "text-gray-300 hover:text-white"
              }`}
            >
              {l}
            </button>
          ))}
        </div>
        <span className="w-px h-4 bg-white/30" aria-hidden />
        <button
          type="button"
          onClick={exit}
          aria-label="Exit presentation"
          className="text-gray-300 hover:text-white text-xl leading-none -mt-0.5"
        >
          ×
        </button>
      </div>

      {/* Content: loading · video · empty-state */}
      {loading ? (
        <p className="text-gray-400 text-sm">Loading episode…</p>
      ) : vimeoId ? (
        // Full-bleed 16:9, centered, sized as large as fits the viewport.
        <div className="w-full h-full flex items-center justify-center">
          <div className="w-full max-w-[177.78vh] aspect-video">
            <iframe
              src={`https://player.vimeo.com/video/${vimeoId}?autoplay=1&title=0&byline=0&portrait=0`}
              className="w-full h-full"
              frameBorder="0"
              allow="autoplay; fullscreen; picture-in-picture"
              allowFullScreen
              title="Episode"
            />
          </div>
        </div>
      ) : (
        <p className="text-gray-300 text-center px-6 text-lg">
          No episode for this chapter at {tier} · {lang.toUpperCase()}.
        </p>
      )}
    </div>
  );
};

export default PresentationRoute;
